import React, { useCallback, useEffect, useRef, useState } from "react";
import "./OptimizedImage.scss";
import { usePrevious } from "../../hooks/usePrevious";
import debounce from "lodash/debounce";
import { ImagePlaceholder } from "../ImagePlaceholder";
import classNames from "classnames";
import { getWindow } from "../../utils";

export type TImageFit = "contain" | "cover";

type TImage = {
    path?: string;
    aspectX?: number;
    aspectY?: number;
    className?: string;
    imageClassName?: string;
    onImageLoaded?: () => void;
    usePlaceholder?: boolean;
    lazyPlaceholder?: boolean;
    fillParent?: boolean;
    fallbackUrl?: string;
    imageFit?: TImageFit;
    altText?: string;
};

const imageExtension = ".jpg";
const imagePlaceholderPreset = "placeholder";
const imageWidths = [200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000];
const placeholderTimeoutTimeInMs = 100;

const getIsUrlExternal = (url: string) => {
    return !url.includes("static.r360");
};

const getIsPathValid = (path: string | undefined): boolean => {
    const pathParts = (path || "").split("/");

    if (pathParts.length === 3 && pathParts[1] === "images") {
        return true;
    }

    return false;
};

export const getOptimizedImagePathFromHash = (hash?: string): string | undefined => {
    if (!hash) {
        return;
    }

    return `${getWindow().__SITE__}/images/${hash}`;
};

/**
 * Extract the path-part from an image URL.
 * E.g. https://static.r360online.se/support1/images/0e321f4d78ed9df605ce5eba6be57100_org.jpg _> support1/images/0e321f4d78ed9df605ce5eba6be57100
 */
export const getOptimizedImagePathFromUrl = (url?: string): string | undefined => {
    const urlParts = (url || "").split("/").slice(-3);

    if (url && getIsUrlExternal(url)) {
        return url;
    }

    if (urlParts.length === 3 && urlParts[1] === "images") {
        return `${urlParts[0]}/${urlParts[1]}/${urlParts[2].split("_")[0]}`;
    }

    return;
};

export const getOptimizedImageUrlFromImageUrlAndWidth = (
    url: string | undefined,
    width: number
): string | undefined => {
    if (!url) {
        return;
    }

    // Take devicePixelRatio into consideration. Ratio > 1 is used on e.g. Apple Retina Displays.
    const currentRenderedWithPixelDensity = width * (window.devicePixelRatio || 1);
    const imageWidthToUse =
        imageWidths.filter(imageWidth => imageWidth >= currentRenderedWithPixelDensity)[0] ||
        imageWidths[imageWidths.length - 1];
    const preset = `${imageWidthToUse}w`;
    const path = getOptimizedImagePathFromUrl(url);

    if (!path) {
        return;
    }

    return getImageUrlFromPath(path, preset);
};

/**
 * Get the full URL of image to show with correct size.
 *
 * @param path In format support1/images/0e321f4d78ed9df605ce5eba6be57100
 * @param preset Size preset to use.
 * @returns Full image url.
 */
const getImageUrlFromPath = (path: string, preset: string): string => {
    if (!getIsPathValid(path)) {
        return path;
    }

    return getWindow().__BASE_IMAGE_URL__ + path + "_" + preset + imageExtension;
};

const OptimizedImageComponent = ({
    path,
    aspectX = 16,
    aspectY = 9,
    className,
    imageClassName,
    onImageLoaded,
    usePlaceholder = true,
    lazyPlaceholder = false,
    fallbackUrl,
    fillParent = false,
    imageFit = "cover",
    altText = "",
}: TImage) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [placeholderImageUrl, setPlaceholderImageUrl] = useState<string>();
    const placeholderTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const previousPath = usePrevious(path);
    const [imageError, setImageError] = useState(false);
    const pathIsValid = path && getIsPathValid(path);

    useEffect(() => {
        // Reset on path change.
        if (previousPath && path !== previousPath) {
            clearTimeout(placeholderTimeoutRef.current);
            setImageLoaded(false);
            setPlaceholderImageUrl(undefined);
            setImageError(false);

            if (imageRef.current) {
                imageRef.current.src = "";
                delete imageRef.current.dataset.presetWidth;
            }
        }
    }, [path, previousPath]);

    useEffect(() => {
        if (!path && fallbackUrl && imageRef.current) {
            imageRef.current.src = fallbackUrl;
        }
    }, [path, fallbackUrl]);

    const loadOptimizedImage = useCallback(
        (currentRenderedWidth: number) => {
            if (!path) {
                return;
            }

            // Find the correct preset for the currentRenderedWidth.
            // Take devicePixelRatio into consideration. Ratio > 1 is used on e.g. Apple Retina Displays.
            const currentRenderedWidthWithPixelDensity = currentRenderedWidth * (window.devicePixelRatio || 1);
            const imageWidthToUse =
                imageWidths.filter(imageWidth => imageWidth >= currentRenderedWidthWithPixelDensity)[0] ||
                imageWidths[imageWidths.length - 1];

            const currentLoadedPresetWidth = parseInt(imageRef.current?.dataset?.presetWidth || "0", 10);

            if (imageWidthToUse > currentLoadedPresetWidth && imageRef.current) {
                const preset = `${imageWidthToUse}w`;
                // Set src immediately to speed up loading of image.
                imageRef.current.src = getImageUrlFromPath(path, preset);
                imageRef.current.dataset.presetWidth = imageWidthToUse.toString();

                // Set a timeout to determine if placeholder should be shown or not.
                if (!placeholderImageUrl && usePlaceholder && pathIsValid) {
                    clearTimeout(placeholderTimeoutRef.current);
                    placeholderTimeoutRef.current = setTimeout(() => {
                        setPlaceholderImageUrl(getImageUrlFromPath(path, imagePlaceholderPreset));
                    }, placeholderTimeoutTimeInMs);
                }
            }
        },
        [path, placeholderImageUrl, usePlaceholder, pathIsValid]
    );

    const loadOptimizedImageDebounced = useCallback(debounce(loadOptimizedImage, 500), [loadOptimizedImage]);

    // Start observing the element when the component is mounted.
    useEffect(() => {
        if (!imageRef?.current || !path) return;

        const width = imageRef.current.getBoundingClientRect().width;

        // Initial load to avoid waiting for observer.
        if (width > 0) {
            loadOptimizedImage(width);
        }

        const observer = new ResizeObserver(e => {
            const width = e[0]?.contentRect?.width || 0;

            if (width > 0) {
                loadOptimizedImageDebounced(width);
            }
        });

        observer.observe(imageRef.current);

        return () => observer.disconnect();
    }, [loadOptimizedImage, loadOptimizedImageDebounced, path]);

    const handleLoad = () => {
        // Try to determine if image is cached in the browser or not.
        // If the image is loaded within 100ms, we guess that it's cached.
        // Only show the blurred placeholder image if image is NOT cached.

        // Check if placeholder timeout is still active.
        // If so, 100ms hasn't ellapsed yet and the image can be considered cached.
        // Clear timeout.
        if (placeholderTimeoutRef.current) {
            clearTimeout(placeholderTimeoutRef.current);
        }

        setImageLoaded(true);
        onImageLoaded?.();
    };

    const optimizedImageClassName = classNames("c-optimized-image", className);
    const aspectRatio = !fillParent ? `${aspectX}/${aspectY}` : undefined;

    if (imageError || (!path && !fallbackUrl)) {
        return <ImagePlaceholder aspectRatio={`${aspectX}/${aspectY}`} />;
    } else if (!path && fallbackUrl) {
        return (
            <div
                className={optimizedImageClassName}
                style={{
                    backgroundColor: "#f8f8f8",
                    aspectRatio,
                    height: fillParent ? "100%" : "auto",
                }}
            >
                <img
                    ref={imageRef}
                    className={
                        "c-optimized-image__image c-optimized-image__image-fade-in" +
                        (imageClassName ? ` ${imageClassName}` : "")
                    }
                    style={{ opacity: imageLoaded ? 1 : 0, objectFit: imageFit }}
                    src={undefined}
                    loading="lazy"
                    onLoad={handleLoad}
                    alt={altText}
                />
            </div>
        );
    } else {
        return (
            <div
                className={optimizedImageClassName}
                style={{
                    aspectRatio,
                    height: fillParent ? "100%" : "auto",
                }}
            >
                {placeholderImageUrl && (
                    <img
                        className="c-optimized-image__image"
                        loading={lazyPlaceholder ? "lazy" : "eager"}
                        src={placeholderImageUrl}
                        style={{ objectFit: imageFit }}
                        alt={altText}
                    />
                )}
                <img
                    ref={imageRef}
                    className={
                        "c-optimized-image__image" +
                        (placeholderImageUrl ? " c-optimized-image__image-fade--in" : "") +
                        (imageClassName ? ` ${imageClassName}` : "")
                    }
                    style={{ opacity: imageLoaded ? 1 : 0, objectFit: imageFit }}
                    onError={() => setImageError(true)}
                    loading="lazy"
                    src={undefined}
                    onLoad={handleLoad}
                    alt={altText}
                />
            </div>
        );
    }
};

export const OptimizedImage = React.memo(OptimizedImageComponent);
